import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bars3Icon, XMarkIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

import { ESGLedgerLogo } from './ESGLedgerLogo'

interface LayoutProps {
  children: React.ReactNode
  portalType?: 'regulators' | 'suppliers' | 'auditors' | 'retailers' | 'public' | 'admin'
}

const Layout: React.FC<LayoutProps> = ({ children, portalType }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const { user, logout } = useAuth()

  const getPortalTitle = () => {
    switch (portalType) {
      case 'regulators':
        return 'Government Regulators Portal'
      case 'suppliers':
        return 'Suppliers Portal - OneChain Clients'
      case 'auditors':
        return 'Auditors Portal'
      case 'retailers':
        return 'Retailers Portal'
      case 'public':
        return 'Public Transparency Portal'
      case 'admin':
        return 'System Administration'
      default:
        return 'Blockchain Recycling Reporting System'
    }
  }

  const getPortalColor = () => {
    return 'from-primary-600 via-primary-500 to-primary-700'
  }

  const getNavigationItems = () => {
    if (!user) return []
    
    // Admin only sees admin dashboard
    if (user.role === 'admin') {
      return [
        { name: 'Admin Dashboard', href: '/admin' }
      ]
    }

    // Role-specific navigation for other users
    switch (user.role) {
      case 'gov':
        return [{ name: 'Dashboard', href: '/regulators' }]
      case 'suppliers':
        return [{ name: 'Supplier Dashboard', href: '/suppliers' }]
      case 'auditors':
        return [{ name: 'Audit Dashboard', href: '/auditors' }]
      case 'retailers':
        return [{ name: 'Retailer Dashboard', href: '/retailers' }]
      case 'public':
        return [{ name: 'Public Portal', href: '/public' }]
      default:
        return []
    }
  }

  const navigationItems = getNavigationItems()

  const handleLogout = () => {
    logout()
    setIsMobileMenuOpen(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-lg relative z-50 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <ESGLedgerLogo className="h-10" enlarged />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith(item.href) && item.href !== '/' ? 'text-primary-600 bg-primary-50' : ''
                  } ${location.pathname === item.href && item.href === '/' ? 'text-primary-600 bg-primary-50' : ''}`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              {user ? (
                <div className="flex items-center space-x-4">
                  <div className="hidden md:block text-right">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.organization}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Sign Out</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  Sign In
                </Link>
              )}
              
              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden text-gray-700 hover:text-primary-600"
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-navigation"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div
            id="mobile-navigation"
            className={`md:hidden grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
              isMobileMenuOpen
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0 pointer-events-none'
            }`}
          >
            <div className="overflow-hidden">
              <div className={`pb-4 transition-transform duration-300 ease-out ${
                isMobileMenuOpen ? 'translate-y-0' : '-translate-y-2'
              }`}>
                <div className="space-y-1">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        location.pathname.startsWith(item.href) && item.href !== '/' 
                          ? 'text-primary-600 bg-primary-50' 
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      } ${location.pathname === item.href && item.href === '/' ? 'text-primary-600 bg-primary-50' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                  {user && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.organization}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Portal Title Bar */}
      {portalType && (
        <div className={`bg-gradient-to-r ${getPortalColor()} text-white py-4 print:hidden`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold">{getPortalTitle()}</h2>
            <p className="text-white/80 mt-1">Immutable Recycling Data & Audit Trails</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="inline-flex mb-4">
                <ESGLedgerLogo className="h-12 md:h-14" enlarged variant="dark" />
              </div>
              <p className="text-gray-300">Blockchain-powered recycling reporting system for transparency and accountability.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Portals</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/regulators" className="hover:text-white transition-colors">Government Regulators</Link></li>
                <li><Link to="/suppliers" className="hover:text-white transition-colors">Suppliers</Link></li>
                <li><Link to="/auditors" className="hover:text-white transition-colors">Auditors</Link></li>
                <li><Link to="/public" className="hover:text-white transition-colors">Public Portal</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Resources</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Connect</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors">GitHub</a></li>
                <li><a href="#" className="hover:text-white transition-colors">LinkedIn</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Twitter</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Newsletter</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 text-center text-gray-400 border-t border-gray-700">
            <p>&copy; 2026 ESGLedger. All rights reserved. Powered by blockchain technology.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout
